/**
 * useClinicalHud3LiveAccess — gate for ClinicalHUD3 Live mode.
 *
 * Returns loading, isAdmin (JWT global admin via useAuth), hasGrant,
 * hasAttestation, canEnterLive, and needsAttestation.
 *
 * Does NOT read clinicalHudAccess or chartMindI2Access.
 */
import { useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import {
  subscribeToClinicalHud3LiveAccess,
  subscribeToClinicalHud3LiveAttestation,
} from "./clinicalHud3LiveAccess";

export function useClinicalHud3LiveAccess() {
  const { user, isGlobalAdmin, userLoading } = useAuth();
  const [hasGrant, setHasGrant] = useState(false);
  const [hasAttestation, setHasAttestation] = useState(false);
  const [grantLoading, setGrantLoading] = useState(true);
  const [attestationLoading, setAttestationLoading] = useState(true);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;
  const isAdmin = Boolean(isGlobalAdmin);

  useEffect(() => {
    if (userLoading) {
      setGrantLoading(true);
      setAttestationLoading(true);
      return undefined;
    }

    if (!isRealAccount || isAdmin) {
      setHasGrant(false);
      setHasAttestation(false);
      setGrantLoading(false);
      setAttestationLoading(false);
      return undefined;
    }

    setGrantLoading(true);
    setAttestationLoading(true);

    const unsubGrant = subscribeToClinicalHud3LiveAccess(uid, (data) => {
      setHasGrant(Boolean(data));
      setGrantLoading(false);
    });
    const unsubAttestation = subscribeToClinicalHud3LiveAttestation(
      uid,
      (data) => {
        setHasAttestation(Boolean(data));
        setAttestationLoading(false);
      },
    );

    return () => {
      unsubGrant();
      unsubAttestation();
    };
  }, [uid, isRealAccount, userLoading, isAdmin]);

  const loading = Boolean(
    userLoading ||
      (isRealAccount && !isAdmin && (grantLoading || attestationLoading)),
  );

  const canEnterLive = Boolean(isAdmin || (hasGrant && hasAttestation));
  const needsAttestation = Boolean(!isAdmin && hasGrant && !hasAttestation);

  return {
    loading,
    isAdmin,
    hasGrant: loading ? false : hasGrant,
    hasAttestation: loading ? false : hasAttestation,
    canEnterLive: loading ? false : canEnterLive,
    needsAttestation: loading ? false : needsAttestation,
  };
}

export default useClinicalHud3LiveAccess;
